/** @jsxImportSource @emotion/core */
import { css } from '@emotion/core';
import React, { useRef } from 'react';
import {
	MdKeyboardArrowLeft,
	MdKeyboardArrowRight,
	MdRefresh,
	MdZoomIn,
	MdZoomOut,
} from 'react-icons/md';
import {
	TransformWrapper,
	TransformComponent,
	ReactZoomPanPinchRef,
} from 'react-zoom-pan-pinch';
import useMeasure from 'react-use-measure';

import { Box, Flex } from 'components/styled';
import Text from 'components/styled/Text';
import ModalDialog from 'components/modal';
import IconButton from 'components/styled/IconButton';

import { CrossIcon } from 'assets';
import { Theme } from 'theme';

type Props = {
	control: (openModal: () => void) => React.ReactNode;
	onNext?: () => void;
	onPrev?: () => void;
	isFirstImage?: boolean;
	isLastImage?: boolean;
	src: string;
	description?: string;
	title?: string;
	hideArrows?: boolean;
};

const Gallery: React.FC<Props> = ({
	control,
	onNext,
	onPrev,
	isFirstImage,
	isLastImage,
	src,
	description,
	title,
	hideArrows,
}) => {
	const [ref, { width, height }] = useMeasure({
		debounce: 10,
	});

	const pinchRef = useRef<ReactZoomPanPinchRef>(null);

	const handlePageChange = (change?: () => void) => {
		pinchRef.current?.resetTransform();
		change?.();
	};

	return (
		<ModalDialog
			label={title ?? src}
			control={control}
			customCss={(theme: Theme) => css`
				width: 100%;
				padding: 0 !important;
				margin: 0;
				min-width: ${theme.breakpoints[0]};
				@media (max-width: ${theme.breakpoints[0]}) {
					width: 100% !important;
					min-width: unset;
				}
			`}
		>
			{closeModal => (
				<Box>
					<Flex alignItems="center" flexDirection="row" bg="rgba(0,0,0,0.65)">
						{!hideArrows && (
							<Box
								flex="0.5 0"
								alignSelf="flex-start"
								position="sticky"
								top={0}
							>
								<Flex
									flexShrink={0}
									color={isFirstImage ? 'textLight' : 'white'}
									css={css`
										cursor: ${isFirstImage ? 'not-allowed' : 'pointer'};
										height: calc(100vh - 50px); // 100vh;
										align-items: center;
										justify-content: center;

										&:hover {
											background-color: ${!isFirstImage &&
											'rgba(0, 0, 0, 0.1)'};
										}
									`}
									onClick={() => !isFirstImage && handlePageChange(onPrev)}
								>
									<MdKeyboardArrowLeft size={100} />
								</Flex>
							</Box>
						)}

						<Box flex="7 0">
							<Box ref={ref} flexGrow={1} maxHeight="100vh">
								{(title || description) && (
									<Box
										position="absolute"
										bg="white"
										top={0}
										left={0}
										maxWidth={300}
										width={300}
										p={2}
										m={2}
										zIndex={1}
										css={css`
											box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
										`}
									>
										{title && (
											<Text
												fontSize="lg"
												textAlign="center"
												color="primary"
												fontWeight="bold"
											>
												{title}
											</Text>
										)}
										{description && (
											<Text textAlign="center">{description}</Text>
										)}
									</Box>
								)}

								<TransformWrapper
									ref={pinchRef}
									initialScale={1}
									limitToBounds={true}
									minScale={0.8}
									maxScale={8}
									centerZoomedOut={true}
									centerOnInit={true}
								>
									<TransformComponent>
										<div
											css={css`
												cursor: grab;
												background: rgba(0, 0, 0, 0);
												min-width: 360px;
												min-height: 90vh;
												background-position: center;
												background-size: contain;
												background-repeat: no-repeat;
												display: flex;
												justify-content: center;
												align-items: center;
												background-image: url(${src});
											`}
											style={{
												width: width * 0.99,
												height: `calc(${height}px - 50px)`,
											}}
										/>
									</TransformComponent>
								</TransformWrapper>
								{/* <img alt="Náhled" src={src} width="100%" height="100%" /> */}
							</Box>
						</Box>
						<Box flex="0.5 0" alignSelf="flex-start" position="sticky" top={0}>
							<Flex
								flexShrink={0}
								color={isLastImage ? 'textLight' : 'white'}
								css={
									hideArrows
										? css`
												height: calc(100vh - 50px); // 100vh;
										  `
										: css`
												cursor: ${isLastImage ? 'not-allowed' : 'pointer'};
												height: calc(100vh - 50px); // 100vh;
												align-items: center;
												justify-content: center;

												&:hover {
													background-color: ${!isLastImage &&
													'rgba(0, 0, 0, 0.1)'};
												}
										  `
								}
								onClick={() => !isLastImage && handlePageChange(onNext)}
							>
								{!hideArrows && <MdKeyboardArrowRight size={100} />}
							</Flex>

							<Box
								p={[2, 3]}
								css={css`
									cursor: pointer;
									position: absolute;
									top: 10px;
									right: 10px;
									&:hover {
										background-color: rgba(0, 0, 0, 0.2);
									}
								`}
							>
								<CrossIcon
									size={[30, 45]}
									color="white"
									onClick={() => closeModal()}
								/>
							</Box>
						</Box>
					</Flex>
					<Flex
						bg="white"
						height="50px"
						alignItems="center"
						justifyContent="center"
					>
						<IconButton
							mx={2}
							size="medium"
							onClick={() => pinchRef.current?.zoomOut()}
						>
							<Box px={2}>
								<MdZoomOut size={40} />
							</Box>
						</IconButton>
						<IconButton
							mx={2}
							size="medium"
							onClick={() => pinchRef.current?.zoomIn()}
						>
							<Box px={2}>
								<MdZoomIn size={40} />
							</Box>
						</IconButton>
						<IconButton
							mx={2}
							size="medium"
							onClick={() => pinchRef.current?.resetTransform()}
						>
							<Box px={2}>
								<MdRefresh size={40} />
							</Box>
						</IconButton>
					</Flex>
				</Box>
			)}
		</ModalDialog>
	);
};

export default Gallery;
